import React, { useState, useEffect } from 'react';
import { useBed } from '../../../context/BedContext';
import { useTCoins } from '../../../context/TCoinsContext';
import { useAuth } from '../../../context/AuthContext';
import { TEAM_CONFIG_PRICING } from '../../../data/defaultTeamConfigs';

const AddEditBedModal = ({ isOpen, onClose, bed, wards, departments, userPlan, totalTCoins }) => {
  const { addBed, updateBed } = useBed();
  const { deductTCoins } = useTCoins();
  const { currentUser } = useAuth();

  const [formData, setFormData] = useState({
    bedNumber: '',
    bedName: '',
    bedLocation: '',
    ward: '',
    department: '',
    floor: '',
    room: '',
    status: 'AVAILABLE'
  });

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (bed) {
      setFormData({
        bedNumber: bed.bedNumber || '',
        bedName: bed.bedName || '',
        bedLocation: bed.bedLocation || '',
        ward: bed.ward || '',
        department: bed.department || '',
        floor: bed.floor || '',
        room: bed.room || '',
        status: bed.status || 'AVAILABLE'
      });
    } else {
      setFormData({
        bedNumber: '',
        bedName: '',
        bedLocation: '',
        ward: '',
        department: '',
        floor: '',
        room: '',
        status: 'AVAILABLE'
      });
    }
    setError('');
  }, [bed, isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!formData.bedNumber.trim()) {
      setError('กรุณากรอกหมายเลขเตียง');
      return;
    }

    if (!formData.bedName.trim()) {
      setError('กรุณากรอกชื่อเตียง');
      return;
    }

    if (!formData.ward) {
      setError('กรุณาเลือกหอผู้ป่วย');
      return;
    }

    if (!formData.department) {
      setError('กรุณาเลือกแผนก');
      return;
    }

    // Check pricing for new bed
    if (!bed && userPlan !== 'PRO' && totalTCoins < TEAM_CONFIG_PRICING.ADD_BED) {
      setError(`คุณต้องมี T Coins อย่างน้อย ${TEAM_CONFIG_PRICING.ADD_BED} เหรียญ หรือสมัคร Pro Plan`);
      return;
    }

    if (!bed && !window.confirm(`เพิ่มเตียง "${formData.bedName}"?\n${userPlan !== 'PRO' ? `ค่าใช้จ่าย: ${TEAM_CONFIG_PRICING.ADD_BED} T Coins` : 'ฟรี (Pro Plan)'}`)) {
      return;
    }

    setSaving(true);
    try {
      let result;
      if (bed) {
        // Update existing bed
        result = await updateBed(bed.id, formData);
      } else {
        // Add new bed
        result = await addBed(formData);

        // Deduct T Coins if not Pro
        if (result.success && userPlan !== 'PRO') {
          const deducted = await deductTCoins(currentUser.uid, 'real', TEAM_CONFIG_PRICING.ADD_BED, `เพิ่มเตียง ${formData.bedName}`);
          if (!deducted) {
            setError('ไม่สามารถหัก T Coins ได้');
            setSaving(false);
            return;
          }
        }
      }

      if (result.success) {
        alert(bed ? 'แก้ไขเตียงสำเร็จ' : 'เพิ่มเตียงสำเร็จ');
        onClose();
      } else {
        setError(result.error || 'เกิดข้อผิดพลาด');
      }
    } catch (err) {
      console.error('Error saving bed:', err);
      setError(err.message || 'เกิดข้อผิดพลาด');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <h2 className="text-xl font-bold text-gray-800 mb-4">
          {bed ? '✏️ แก้ไขเตียง' : '+ เพิ่มเตียงใหม่'}
        </h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Bed Number */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                หมายเลขเตียง *
              </label>
              <input
                type="text"
                value={formData.bedNumber}
                onChange={(e) => setFormData({ ...formData, bedNumber: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="เช่น 001"
                required
              />
            </div>

            {/* Bed Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                ชื่อเตียง *
              </label>
              <input
                type="text"
                value={formData.bedName}
                onChange={(e) => setFormData({ ...formData, bedName: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="เช่น เตียง 1"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Ward */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                หอผู้ป่วย *
              </label>
              <select
                value={formData.ward}
                onChange={(e) => setFormData({ ...formData, ward: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="">-- เลือกหอผู้ป่วย --</option>
                {wards.map(ward => (
                  <option key={ward} value={ward}>{ward}</option>
                ))}
              </select>
            </div>

            {/* Department */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                แผนก *
              </label>
              <select
                value={formData.department}
                onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="">-- เลือกแผนก --</option>
                {departments.map(dept => (
                  <option key={dept} value={dept}>{dept}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Bed Location */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              ตำแหน่งวางเตียง
            </label>
            <input
              type="text"
              value={formData.bedLocation}
              onChange={(e) => setFormData({ ...formData, bedLocation: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="เช่น ห้อง 101, ชั้น 1"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Floor */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                ชั้น (ไม่บังคับ)
              </label>
              <input
                type="text"
                value={formData.floor}
                onChange={(e) => setFormData({ ...formData, floor: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="เช่น 1"
              />
            </div>

            {/* Room */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                ห้อง (ไม่บังคับ)
              </label>
              <input
                type="text"
                value={formData.room}
                onChange={(e) => setFormData({ ...formData, room: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="เช่น 101"
              />
            </div>
          </div>

          {/* Status (only for edit) */}
          {bed && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                สถานะ
              </label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="AVAILABLE">🟢 ว่าง</option>
                <option value="IN_USE">🔴 ใช้งานอยู่</option>
                <option value="MAINTENANCE">🟡 ซ่อมบำรุง</option>
                <option value="UNAVAILABLE">⚫ ไม่พร้อมใช้งาน</option>
              </select>
            </div>
          )}

          {!bed && userPlan !== 'PRO' && (
            <p className="text-xs text-gray-500">
              💰 ค่าใช้จ่าย: {TEAM_CONFIG_PRICING.ADD_BED} T Coins (Pro Plan ฟรี)
            </p>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition"
            >
              ยกเลิก
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition"
            >
              {saving ? 'กำลังบันทึก...' : bed ? 'บันทึกการแก้ไข' : 'เพิ่มเตียง'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddEditBedModal;



